import { Component, OnInit, TemplateRef, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { AlertType, ModalComponent, ModalResult, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { CustomerInfoCam, PaginatedCustomerInfoResponse } from 'src/app/features/customer/models/response.model';
import { UpdateCurrentPaginatedSpecificationsAction, UpdateEditingModeAction } from 'src/app/shared/state/AssetManagementState';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { ColumnPreferencesService } from '../../../../shared/services/column-preferences.service';
import { ColumnPreferencesHelperService } from '../../../../shared/services/column-preferences-helper.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { getDateInMMddyyyyFormat } from '../../../../shared/helper/date-time-helper';
import { DealerState } from '../../../../shared/state/DealerState';
import { AssetManagementService } from '../../../asset-management/services/asset-management.service';
import { canEditCamSpec, specCodeValidator } from '../../../cam-specification/utils/SpecificationUtils';
import { CamSpec } from '../../model/camspec.model';
import { PaginatedCamSpecResponse } from '../../model/paginated-camspec.model';
import { Pagination } from '../../model/pagination.model';
import { UtilityService } from '../../../../shared/services/utility.service';

@Component({
  selector: 'app-search-cam-spec',
  templateUrl: './search-cam-spec.component.html',
  styleUrls: ['./search-cam-spec.component.scss'],
  standalone: false
})
export class SearchCamSpecComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild(TableComponent, { static: true }) customersRef: TableComponent<Element>;
  @ViewChild('infoModal', { static: true }) infoModal: ModalComponent;
  @ViewChild('saveDuplicate', { static: true }) saveDuplicate: ModalComponent;
  @ViewChild('viewAssigned', { static: true }) viewAssigned: ModalComponent;
  @ViewChild('confirmation') contentModal: TemplateRef<any>;
  @ViewChild('duplicate') duplicateContent: TemplateRef<any>;
  @ViewChild('assigned') assignedContent: TemplateRef<any>;
  @ViewChild('deleteModal', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteConfirmation') deleteContentModal: TemplateRef<any>;
  protected translationService = inject(TranslationService);
  private readonly store = inject(Store);
  private readonly assetManagementService = inject(AssetManagementService);
  private readonly utilityService = inject(UtilityService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly columnPrefService = inject(ColumnPreferencesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly columnPrefsHelper = inject(ColumnPreferencesHelperService);

  search = new FormControl<string>('');
  camSpecs: CamSpec[] = [];
  assignCustomers: CustomerInfoCam[] = [];
  customerPagination: Pagination;
  pagination: Pagination;
  selection: CamSpec[] = [];
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  specName = new FormControl<string>('', [Validators.required]);
  specDescription = new FormControl<string>('', [Validators.required]);
  currentCamSpec: CamSpec;
  camSpecName = '';
  hasEditPermission = true;
  NAFA_DEALER = 'NAFA';
  NAFA_DEALER_ID = 'C1C15DAE-F42A-11EF-A7E5-002248507BA3';
  isMrtDealer = false;
  isSavingDuplicate = false;
  isNafaModal = false;

  casingCreditBreadcrumbs = [
    { link: '/home/dashboard', icon: 'home' },
    { link: '/home/specifications', label: this.translationService.translate('camSpecsText') }
  ];

  additionalColumns = [
    { property: 'casingCreditCode', headerKey: this.translationService.translate('casingCreditCodeText') },
    { property: 'casingCreditDesc', headerKey: this.translationService.translate('casingCreditDescText') },
    { property: 'CustomerInfoCam', headerKey: this.translationService.translate('billToText') },
    { property: 'CustomerInfoCam', headerKey: this.translationService.translate('shipToText') },
    { property: 'CustomerInfoCam', headerKey: this.translationService.translate('cityText') },
    { property: 'CustomerInfoCam', headerKey: this.translationService.translate('stateText') },
    { property: 'CustomerInfoCam', headerKey: this.translationService.translate('camSpecCodeText') },
    { property: 'CustomerInfoCam', headerKey: this.translationService.translate('statusColumnText') }
  ];

  tableConfig: TableConfig<CamSpec> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      //bottomAndTopPaginatorThreshold: 5,
      forceBottomAction: true,
      position: 'bottom'
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {},
    onRowClick: (elm: CamSpec) => {
      this.router.navigateByUrl(`home/specifications/${elm.id}`);
    }
  };

  private canonicalColumns: Record<string, any> = {};
  columnKeys: string[] = [];
  columnNames: Record<string, string> = {};
  columnOrder: string[] = [];
  columnVisibility: Record<string, boolean> = {};

  tableConfig2: TableConfig<CustomerInfoCam> = {
    paginator: {
      type: 'scroll',
      pageSize: 10,
      loadMoreButtonLabel: 'Load more',
      forceBottomAction: true,
      position: 'bottom'
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };

  fetcher: PaginatorFetcher<CamSpec> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    return this.assetManagementService.searchCamSpec(this.search.value, filters.page, filters.limit).pipe(
      map((camSpecResponse: PaginatedCamSpecResponse) => {
        if (camSpecResponse != null && camSpecResponse.error != null && camSpecResponse.error.length > 0) {
          // TODO: change the dialog and content once the error dialog design is finalized
          this.utilityService.showAlert(AlertType.strong, '', 'Please try again. Something went wrong.');
          return;
        } else {
          this.camSpecs = camSpecResponse.data.camSpecs;
          this.pagination = camSpecResponse.data.pagination;
          this.store.dispatch(new UpdateCurrentPaginatedSpecificationsAction(this.camSpecs));
          return {
            items: this.camSpecs,
            count: this.pagination.totalRecords
          };
        }
      })
    );
  };

  constructor() {
    this.store.dispatch(new UpdateEditingModeAction(false));
  }

  ngOnInit(): void {
    // check if the logged-in user has cam edit privilege
    const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.hasEditPermission = canEditCamSpec(false, false, dealer);

    if (dealer.name === this.NAFA_DEALER) {
      this.isMrtDealer = true;
      this.specName = new FormControl<string>('BT');
    }

    this.search.valueChanges
      .pipe(
        debounceTime(1000),
        map((search: string) => search?.trim() || ''),
        distinctUntilChanged()
      )
      .subscribe((search: string) => {
        if (search.length >= 3) {
          this.tableRef.patchExtraFilters({ search });
        } else if (search.length === 0) {
          this.tableRef.patchExtraFilters({ search: null });
        }
      });

    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      // build canonical columns using translated headers
      this.canonicalColumns = {
        name: {
          cell: (elm: CamSpec) => elm.name.toString(),
          header: this.translationService.translate('camSpecNameColumnText'),
          sortable: true
        },
        description: {
          cell: (elm: CamSpec) => elm.description,
          header: this.translationService.translate('camSpecDescriptionColumnText'),
          sortable: true
        },
        type: { cell: (elm: CamSpec) => elm.type.toString(), header: this.translationService.translate('typeColumnText') },
        status: {
          cell: (elm: CamSpec) => this.translationService.translate(elm.status.toString() === 'Active' ? 'activeText' : 'inactiveText'),
          header: this.translationService.translate('statusColumnText'),
          display: dealer.name !== this.NAFA_DEALER
        },
        exceeds: {
          cell: (elm: CamSpec) => this.translationService.translate(elm.exceeds.toString() === 'Yes' ? 'yesText' : 'noText'),
          header: this.translationService.translate('exceedsColumnText')
        },
        usedBy: {
          cell: (elm: CamSpec) => elm.usedBy,
          header: this.translationService.translate('usedByColumnText'),
          display: dealer.name !== this.NAFA_DEALER
        },
        createdOn: {
          cell: (elm: CamSpec) => getDateInMMddyyyyFormat(elm.createdDate),
          header: this.translationService.translate('createdOnColumnText')
        },
        createdBy: {
          cell: (elm: CamSpec) => elm.createdBy,
          header: this.translationService.translate('createdByColumnText')
        },
        updatedOn: {
          cell: (elm: CamSpec) => getDateInMMddyyyyFormat(elm.updatedDate),
          header: this.translationService.translate('updatedOnColumnText')
        },
        updatedBy: {
          cell: (elm: CamSpec) => elm.updatedBy,
          header: this.translationService.translate('updatedByColumnText')
        }
      };

      // columnNames map for selector labels
      this.columnKeys = Object.keys(this.canonicalColumns);
      this.columnNames = this.columnKeys.reduce((acc, k) => {
        acc[k] = (this.canonicalColumns[k] && this.canonicalColumns[k].header) || k;
        return acc;
      }, {} as Record<string, string>);

      // initialize columns with preferences
      const init = this.columnPrefsHelper.initialize('search-cam-spec', this.canonicalColumns);
      this.tableConfig.columns = init.tableColumns;
      this.columnNames = init.columnNames;
      this.columnKeys = init.columnKeys;
      this.columnOrder = init.columnOrder;
      this.columnVisibility = init.columnVisibility;
      this.cdr.markForCheck();
    });
  }

  // Toggle visibility for a column and persist preferences (delegates to helper)
  toggleColumn(key: string): void {
    const result = this.columnPrefsHelper.toggleAndSave(
      'search-cam-spec',
      key,
      this.canonicalColumns,
      this.columnOrder,
      this.columnVisibility
    );
    this.tableConfig.columns = result.tableColumns;
    this.columnVisibility = result.columnVisibility;
    this.cdr.markForCheck();
  }

  assignToCustomer(item: any) {
    this.currentCamSpec = item;
    this.router.navigate(['home/specifications/assigncams', this.currentCamSpec.description, this.currentCamSpec.id]);
  }

  duplicateCamSpec(item: any) {
    this.currentCamSpec = item;
    const type = this.isMrtDealer ? this.currentCamSpec.type : 'LOCAL';
    this.specName.setValidators([specCodeValidator(type)]);
    this.showDuplicateModal();
  }

  viewAssignedCustomerList(item: any) {
    this.currentCamSpec = item;
    this.isNafaModal = false;
    this.showInfoModal3();
    this.search.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe((search: string) => {
      this.customersRef.patchExtraFilters({ search });
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig2.columns = {
        name: {
          cell: (elm: CustomerInfoCam) => elm.customerCode.toString(),
          header: this.translationService.translate('customerCodeText'),
          sortable: true
        },
        description: {
          cell: (elm: CustomerInfoCam) => elm.customerName,
          header: this.translationService.translate('customerNameText'),
          sortable: true
        },
        type: { cell: (elm: CustomerInfoCam) => elm.billTo.toString(), header: this.translationService.translate('billToText') },
        status: { cell: (elm: CustomerInfoCam) => elm.shipTo, header: this.translationService.translate('shipToText') },
        exceeds: {
          cell: (elm: CustomerInfoCam) => elm.city.toString(),
          header: this.translationService.translate('cityText')
        },
        usedBy: { cell: (elm: CustomerInfoCam) => elm.state, header: this.translationService.translate('stateText') }
      };
    });
  }

  viewNafaAssignedCustomerList(item: any) {
    this.currentCamSpec = item;
    this.isNafaModal = true;
    this.showInfoModal3();
    this.search.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe((search: string) => {
      this.customersRef.patchExtraFilters({ search });
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig2.columns = {
        name: {
          cell: (elm: CustomerInfoCam) => elm.customerCode.toString(),
          header: this.translationService.translate('customerCodeText'),
          sortable: true
        },
        description: {
          cell: (elm: CustomerInfoCam) => elm.customerName,
          header: this.translationService.translate('customerNameText'),
          sortable: true
        },
        type: { cell: (elm: CustomerInfoCam) => elm.billTo.toString(), header: this.translationService.translate('billToText') },
        status: { cell: (elm: CustomerInfoCam) => elm.shipTo, header: this.translationService.translate('shipToText') },
        exceeds: {
          cell: (elm: CustomerInfoCam) => elm.city.toString(),
          header: this.translationService.translate('cityText')
        },
        usedBy: { cell: (elm: CustomerInfoCam) => elm.state, header: this.translationService.translate('stateText') },
        dealer: { cell: (elm: CustomerInfoCam) => elm.dealerName, header: this.translationService.translate('dealerText') }
      };
    });
  }

  customerTableFetcher: PaginatorFetcher<CustomerInfoCam> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    return this.assetManagementService.searchAssignedCustomers(this.currentCamSpec.id, this.currentPage, this.pageSize).pipe(
      map((customerInfoResponse: PaginatedCustomerInfoResponse) => {
        this.assignCustomers = customerInfoResponse.data.customerInfos;
        this.customerPagination = customerInfoResponse.data.pagination;
        return {
          items: this.assignCustomers,
          count: this.customerPagination.totalRecords
        };
        //}
      })
    );
  };

  nafaCustomerTableFetcher: PaginatorFetcher<CustomerInfoCam> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    return this.assetManagementService.searchNafaAssignedCustomers(this.currentCamSpec.id, this.currentPage, this.pageSize).pipe(
      map((customerInfoResponse: PaginatedCustomerInfoResponse) => {
        this.assignCustomers = customerInfoResponse.data.customerInfos;
        this.customerPagination = customerInfoResponse.data.pagination;
        return {
          items: this.assignCustomers,
          count: this.customerPagination.totalRecords
        };
      })
    );
  };

  onSearchClick() {
    if (this.search.value === '') {
      this.showInfoModal();
      return;
    }
  }

  showInfoModal(): void {
    this.infoModal.open({
      id: 'confirmation',
      width: 500,
      title: this.translationService.translate('searchButtonText'),
      content: this.contentModal,
      confirm: this.translationService.translate('okText')
    });
  }

  showDuplicateModal(): void {
    this.saveDuplicate.open({
      // Use the modal identifier
      id: 'duplicate',
      width: 750,
      title: this.translationService.translate('Duplicate CAM Spec'),
      content: this.duplicateContent
    });
  }

  cancelDulicateCamSpecModal() {
    this.saveDuplicate.dialog.closeAll();
    this.specName.reset();
    this.specDescription.reset();
  }

  onModalClosed() {
    this.specName.reset();
    this.specDescription.reset();
  }

  saveDulicateCamSpec() {
    this.isSavingDuplicate = true;
    const user = this.store.selectSnapshot(DealerState.currentUser);
    if (!this.isMrtDealer) {
      this.currentCamSpec.type = 'LOCAL';
    }
    this.assetManagementService
      .duplicateCamSpec(this.specName.value, this.specDescription.value, this.currentCamSpec, user)
      .subscribe((res: boolean) => {
        this.isSavingDuplicate = false;
        if (!res) {
          this.specName.setErrors({ alreadyExists: true });
          this.specName.markAllAsTouched();
          this.utilityService.showAlert(
            AlertType.strong,
            'Cam Spec name already exists',
            this.translationService.translate('incorrectValuesAlertText')
          );
        } else {
          this.cancelDulicateCamSpecModal();
          this.tableRef.patchExtraFilters(this.tableRef.getFilters());
          this.utilityService.showAlert(
            AlertType.success,
            this.translationService.translate('saveDuplicateSuccessAlertTitleText'),
            this.translationService.translate('saveDuplicateSuccessAlertText')
          );
        }
      });
  }

  showInfoModal3(): void {
    this.viewAssigned.open({
      // Use the modal identifier
      id: 'assigned',
      width: 950,
      title: this.translationService.translate('List of Assigned Customers'),
      content: this.assignedContent
    });
  }

  showDeleteModal(item: any): void {
    this.camSpecName = item.name;
    this.deleteModal.open({
      id: item.id,
      width: 500,
      title: this.translationService.translate('deleteCAMSpecMenuText'),
      content: this.deleteContentModal,
      confirm: {
        text: this.translationService.translate('deleteCAMSpecMenuText'),
        color: 'warn'
      },
      cancel: this.translationService.translate('cancelLinkText')
    });
  }

  onDeleteModalClosed(result: ModalResult) {
    if (result && result.result) {
      this.assetManagementService.deleteInactiveCamSpec(result.id).subscribe({
        next: () => {
          this.tableRef.patchExtraFilters(this.tableRef.getFilters());
        },
        error: (error) => {
          const statusCode: number = error.error?.statusCode;
          const alertType = 400 <= statusCode && statusCode < 500 ? AlertType.strong : AlertType.critical;
          this.utilityService.showAlert(alertType, 'Critical Error', 'Please try again. Something went wrong.');
        }
      });
    }
  }

  // NAFA cams can only be edited by MRT user
  // checks if logged in dealer is MRT and if user has cam edit permission
  canEditNafaCam(isNafaSpec: boolean) {
    const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
    return canEditCamSpec(false, isNafaSpec, dealer);
  }

  canDeleteCamSpec(specType: string) {
    const isNafaSpec = specType === 'NAFA';
    if (isNafaSpec) {
      return false;
    } else {
      const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
      return canEditCamSpec(false, isNafaSpec, dealer);
    }
  }

  hasViewAndAssignCustomerPermission(specType: string) {
    const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
    return !(specType === 'NAFA' && dealer.name === this.NAFA_DEALER);
  }

  isNafaDealerContext(): boolean {
    const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
    return dealer.id?.toUpperCase() === this.NAFA_DEALER_ID.toUpperCase();
  }

  navigateToCreateCamSpec() {
    this.router.navigate(['home/specifications/create']);
  }

  openNafaExportModal(): void {
    const columns = [
      { key: 'customerCode', label: this.translationService.translate('customerCodeText') },
      { key: 'customerName', label: this.translationService.translate('customerNameText') },
      { key: 'billTo', label: this.translationService.translate('billToText') },
      { key: 'shipTo', label: this.translationService.translate('shipToText') },
      { key: 'city', label: this.translationService.translate('cityText') },
      { key: 'state', label: this.translationService.translate('stateText') },
      { key: 'dealerName', label: this.translationService.translate('dealerText') }
    ];
    const dataFetcher = () =>
      this.assetManagementService.searchNafaAssignedCustomers(this.currentCamSpec.id, 1, 10000).pipe(
        map((response: any) => ({
          data: (response?.data?.customerInfos || []) as Record<string, any>[]
        }))
      );
    const data: ExportModalData = {
      columns,
      dataFetcher,
      fileNamePrefix: 'nafa-assigned-customers',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '600px' });
  }

  onNameInput(specNameControl: FormControl): void {
    if (this.isMrtDealer) {
      const specName = specNameControl.value;

      if (!specName.startsWith('BT')) {
        specNameControl.setValue('BT', { emitEvent: false });
      }
    }
  }
}
